import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: "" };
  /** componentStack은 ref에 보관해 setState 트리거 없이 표시 (React #185 재발 방지) */
  private stackRef: string = "";

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // componentStack은 ref에만 저장 (setState 트리거 X — getDerivedStateFromError에서 이미 setState 했음)
    this.stackRef = errorInfo.componentStack || "";
    console.error("[ErrorBoundary]", error.message);
    console.error("[ErrorBoundary] componentStack:", errorInfo.componentStack);
    // forceUpdate 1회 — getDerivedStateFromError 후 render는 이미 됐는데 ref는 비어있으니 한 번 더 render
    if (this.state.hasError && !this.stackRef) return;
    this.forceUpdate();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="bg-card-bg rounded-2xl border border-gray-200 shadow-sm p-6 max-w-md w-full text-center">
            <p className="text-sm text-gray-500 mb-3">문제가 발생했어요</p>
            <p className="text-xs text-gray-400 mb-4 break-all">{this.state.error?.message}</p>
            {this.stackRef && (
              <details className="text-left mb-4">
                <summary className="text-xs text-gray-500 cursor-pointer">기술 정보</summary>
                <pre className="text-[10px] text-gray-400 whitespace-pre-wrap break-all mt-2 max-h-40 overflow-auto bg-gray-50 p-2 rounded">{this.stackRef.slice(0, 1500)}</pre>
              </details>
            )}
            <button
              onClick={() => {
                this.stackRef = "";
                this.setState({ hasError: false, error: null, componentStack: "" });
                window.location.href = "/";
              }}
              className="bg-gray-900 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            >
              홈으로 돌아가기
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
